/**
 * Carousel Slide 1 — "Hook"
 * Hero image + title + date/price + producer name.
 * Visually identical to T1 but with "1/3" indicator.
 */

import React from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import type { ShareCardProps } from '../ShareCardBase';
import {
  FARMLY_COLORS,
  FARMLY_FONTS,
  GradientOverlay,
  FarmlyBadge,
  FallbackBg,
  HeroImage,
} from '../ShareCardBase';

export function CarouselSlide1({ target, format: fmt, spec }: ShareCardProps) {
  const { w, h } = spec;
  const padding = 48;

  let dateLine = '';
  let priceLine = '';

  if (target.type === 'activity') {
    if (target.timingMode === 'open' || target.timingMode === 'anytime') {
      dateLine = 'Öppet under säsong';
    } else if (target.startDate) {
      try {
        dateLine = format(new Date(target.startDate), "d MMMM yyyy · HH:mm", { locale: sv });
      } catch { /* ignore */ }
    } else if (target.timingMode === 'season') {
      dateLine = 'Säsongsaktivitet';
    }
    const price = getPriceCopy({ is_free: target.isFree, price_ore: target.priceOre });
    priceLine = price.primary ?? '';
  }

  return (
    <div style={{ position: 'relative', width: w, height: h, overflow: 'hidden', fontFamily: FARMLY_FONTS.body }}>
      {target.heroImageUrl ? (
        <HeroImage src={target.heroImageUrl} w={w} h={h} />
      ) : (
        <FallbackBg title={target.title} w={w} h={h} />
      )}
      <GradientOverlay />

      {/* Slide indicator */}
      <div style={{
        position: 'absolute', top: 40, right: 40, zIndex: 3,
        background: 'rgba(0,0,0,0.5)', borderRadius: 16,
        padding: '6px 16px', fontSize: 18, color: FARMLY_COLORS.white,
        fontFamily: FARMLY_FONTS.body, fontWeight: 600,
      }}>
        1 / 3
      </div>

      {/* Content */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
        padding, paddingBottom: 80,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{
          fontSize: 18, color: FARMLY_COLORS.warmSand, opacity: 0.85,
          fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {target.producerName}
        </div>
        <div style={{
          fontFamily: FARMLY_FONTS.display, fontSize: 48, lineHeight: 1.15,
          color: FARMLY_COLORS.white, maxWidth: w - padding * 2,
          wordWrap: 'break-word', overflowWrap: 'break-word',
        }}>
          {target.title}
        </div>
        {(dateLine || priceLine) && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
            {dateLine && <span style={{ fontSize: 22, color: FARMLY_COLORS.warmSand, opacity: 0.9 }}>📅 {dateLine}</span>}
            {priceLine && <span style={{ fontSize: 22, color: FARMLY_COLORS.warmSand, opacity: 0.9 }}>💰 {priceLine}</span>}
          </div>
        )}
      </div>
      <FarmlyBadge />
    </div>
  );
}
